import {
    ClassicPreset,
} from 'rete';
import type { UiFlowNode } from '../nodes/UiFlowNode';
import type { NodeKind, NodeSocketKind } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import {
    type FisDataLabelFragment,
} from 'types/db';
import type { MaterialModelNodeControlData } from './SelectMaterialModelControl.vue';

type UiFlowNodeControlBaseConfig = {
    nullable?: boolean;
    label?: string;
    /**
     * The flag for optional determines styling for the label using italics for optional fields
     *
     * In order to get the same styling on the labels for the sockets we need to make our own socket components which I think is in scope eventually
     * NOTE: We _could_ make a custom label component and set the default socket text to an empty string, but I feel like that is a bandaid
     */
    optional?: boolean;
}

export type SelectionItem = {
    label: string;
    value: string | number;
};
export type SelectionItemValue = SelectionItem['value'];

type SelectionControlCfg = UiFlowNodeControlBaseConfig & {
    kind: 'selection';
    placeholder?: string;
    items: SelectionItem[] | (() => SelectionItem[]);
};

export type MaterialModelIdControlCfg = UiFlowNodeControlBaseConfig & {
    placeholder?: string;
    kind: 'materialModelSelect';
    onSelect: (materialModel: MaterialModelNodeControlData) => void;
    getValue: (cfg: UiFlowNodeControl<unknown>['nodeCfg']) => string | null;
};

export type FunctionalFlowSelectControlCfg = UiFlowNodeControlBaseConfig & {
    placeholder?: string;
    kind: 'functionalFlowSelect';
    onSelect: (flowId: string | null) => void;
    getValue: (cfg: UiFlowNodeControl<unknown>['nodeCfg']) => string | null;
};

export type MaterialClassIdControlCfg = UiFlowNodeControlBaseConfig & {
    placeholder?: string;
    kind: 'materialClassSelect';
    onSelect: (materialClassId: string) => void;
    getValue: (cfg: UiFlowNodeControl<unknown>['nodeCfg']) => string | null;
};

export type PerformanceDataLabelIdControlCfg = UiFlowNodeControlBaseConfig & {
    placeholder?: string;
    kind: 'performanceDataCollect.dataLabelId';
    onSelect: (dataLabel: FisDataLabelFragment) => void;
};

export type SelectionControlWithCallbackCfg = UiFlowNodeControlBaseConfig & {
    selectLabel: string;
    kind: 'selectWithCallback';
    onSelect: (value: unknown) => void;
    items: SelectionItem[] | (() => SelectionItem[] | Promise<SelectionItem[]>);
};

export type SelectCalendarUdfOutputCfg = UiFlowNodeControlBaseConfig & {
    kind: 'selectCalendarUdfOutput';
    onSelect: (value: unknown) => void;
};

export type SelectWorkInstructionVersionControlCfg = UiFlowNodeControlBaseConfig & {
    kind: 'selectWorkInstructionVersion';
    onSelect: (workInstructionVersionId: string) => void;
    getValue: (cfg: UiFlowNodeControl<unknown>['nodeCfg']) => string | null;
};

export type SelectProcessControlCfg = UiFlowNodeControlBaseConfig & {
    kind: 'selectProcess';
    onSelect: (processId: string) => void;
    getValue: (cfg: UiFlowNodeControl<unknown>['nodeCfg']) => string | null;
};

type MaterialComparisonCfgControl = UiFlowNodeControlBaseConfig & {
    kind: 'materialComparisonCfg';
}

export type TextControlCfg = UiFlowNodeControlBaseConfig & { kind: 'text', placeholder?: string }

export type TextareaControlCfg = UiFlowNodeControlBaseConfig & { kind: 'textarea', placeholder?: string, rows?: number }

export type DeletableTextCfg = UiFlowNodeControlBaseConfig & {
    placeholder?: string;
    onDelete: () => void;
    kind: 'deletableText';
    onChange: (newValue: string) => void;
    getValue: (cfg: UiFlowNodeControl<unknown>['nodeCfg']) => string | null;
};

export type DeletableTextWithTypeSelectorCfg<T extends NodeSocketKind = NodeSocketKind> = UiFlowNodeControlBaseConfig & {
    onDelete: () => void | Promise<void>;
    valuePlaceholder?: string;
    selectionPlaceholder?: string;
    /** specify a subset of socket data types allowed for this connection, if not provided all socket kinds will appear in the list */
    allowedDataTypes?: NodeSocketKind[];
    kind: 'deletableTextControlWithTypeSelector';
    getValue: (cfg: UiFlowNodeControl<unknown>['nodeCfg']) => {
        newValue: string;
        dataType: T;
    } | null;
    onChange: (payload: {
        newValue: string;
        dataType: T;
    }) => void;
};

export type SelectTabGroupControlCfg = UiFlowNodeControlBaseConfig & {
    // controls if the sync button is shown on the control. Useful for when we want users to manually be able to refetch the tab group config
    useSync: boolean;
    kind: 'selectTabGroup';
    onSelect: (tabGroupId: string) => void;
    getValue: (cfg: UiFlowNodeControl<unknown>['nodeCfg']) => string | null;
};

type UiFlowNodeControlConfigMap = {
    number: UiFlowNodeControlBaseConfig & { kind: 'number', min?: number; max?: number };
    text: TextControlCfg;
    textarea: TextareaControlCfg;
    boolean: UiFlowNodeControlBaseConfig & {
        kind: 'boolean';
        tooltipText?: string;
    };
    button: UiFlowNodeControlBaseConfig & { kind: 'button', label: string, onClick: (control: UiFlowNodeControl<unknown>) => void };
    deletableSocket: UiFlowNodeControlBaseConfig & { kind: 'deletableSocket', label: string, onDelete: () => void, rightAlign?: boolean };
    deploymentParameter: UiFlowNodeControlBaseConfig & { kind: 'deploymentParameter', placeholder?: string };
    selectVar: UiFlowNodeControlBaseConfig & {
        kind: 'selectVar',
        /** whether to include variables from varps */
        allVars?: boolean,
        /** if set, a delete box will appear */
        onDelete?: () => void,
        /** available callback for custom select logic */
        onSelect?: (selectedVariableId: string) => void;
        /** allowed variable types */
        allowedDataTypes?: ('num' | 'str' | 'bool')[];
    };
    selectEvent: UiFlowNodeControlBaseConfig & { kind: 'selectEvent' };
    comparisonOperator: UiFlowNodeControlBaseConfig & { kind: 'comparisonOperator' };
    deletableText: DeletableTextCfg;
    deletableTextControlWithTypeSelector: DeletableTextWithTypeSelectorCfg;
    selection: SelectionControlCfg;
    scriptSelect: UiFlowNodeControlBaseConfig & { kind: 'scriptSelect' };
    'createMaterial.attributes': UiFlowNodeControlBaseConfig & { kind: 'createMaterial.attributes', placeholder?: string; };
    location: UiFlowNodeControlBaseConfig & {
        kind: 'location';
        // declaring this function will be the signal to show the clear button
        onClear?: () => void;
    };
    asset: UiFlowNodeControlBaseConfig & { kind: 'asset', label: string };
    materialModelSelect: MaterialModelIdControlCfg;
    functionalFlowSelect: FunctionalFlowSelectControlCfg;
    materialClassSelect: MaterialClassIdControlCfg;
    'performanceDataCollect.dataLabelId': PerformanceDataLabelIdControlCfg;
    selectWithCallback: SelectionControlWithCallbackCfg;
    selectCalendarUdfOutput: SelectCalendarUdfOutputCfg;
    varValue: UiFlowNodeControlBaseConfig & {
        kind: 'varValue',
        /** if set, the control will only allow values of the specified type to be selected */
        dataType?: 'num' | 'str' | 'bool';
    };
    selectWorkVersionInstruction: SelectWorkInstructionVersionControlCfg;
    materialComparisonCfg: MaterialComparisonCfgControl;
    selectProcess: SelectProcessControlCfg;
    selectTabGroup: SelectTabGroupControlCfg;
}
export type UiFlowNodeControlConfig = UiFlowNodeControlConfigMap[keyof UiFlowNodeControlConfigMap];

export class UiFlowNodeControl<T> extends ClassicPreset.Control {
    /**
     * defines how the user interacts with the control,
     * what `kind` of component to render,
     * and other props the component will want
     */
    cfg: UiFlowNodeControlConfig;
    value: T;
    nodeCfg: UiFlowNode<NodeKind>['cfg'];
    /**
     * affects sorting
     */
    index?: number;
    /**
     * whether this control is used as a socket default, and should be disabled when a socket is connected.
     */
    socketDefault: boolean;

    /** for the node to capture changes */
    changeCb?: (value: T) => void;

    constructor (opts: UiFlowNodeControl.Opts<T>) {
        super();
        this.cfg = opts.cfg;
        this.value = opts.value;
        this.cfg.nullable = opts.nullable ?? false;
        this.nodeCfg = opts.nodeCfg;
        this.index = opts.index;
        this.socketDefault = opts.socketDefault ?? true;
    }

    // expected by rete
    setValue (value: T): void {
        this.value = value;
        if (this.changeCb) {
            this.changeCb(value);
        }
    }
}

export namespace UiFlowNodeControl {
    export interface Opts<T> {
        cfg: UiFlowNodeControlConfig,
        value: T,
        nullable?: boolean,
        nodeCfg: UiFlowNode<NodeKind>['cfg'],
        index?: number,
        /** @default true */
        socketDefault?: boolean,
    }
}
